import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "@mui/material";
import {
  setSelectedImages,
  setModalState,
  selectSelectedImages,
} from "../../../Store/Gmb/gmbImageSlice";
import ImageModal from "./ImageModal";

export default function ImageCarousel({ imageList = [], deleteMode = false }) {
  const dispatch = useDispatch();
  const selectedImages = useSelector(selectSelectedImages);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalState, setModalStateLocal] = useState({
    isOpen: false,
    currentImageIndex: 0,
  });

  // Media query to adjust images per slide
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const isMediumScreen = useMediaQuery("(min-width:600px) and (max-width:1200px)");
  const isLargeScreen = useMediaQuery("(min-width:1200px) and (max-width:1440px)");
  const imagesPerSlide = isSmallScreen ? 1 : isMediumScreen ? 3 : isLargeScreen ? 4 : 6;

  const handleCheckboxChange = (index) => {
    const image = imageList[index];
    const newSelection = selectedImages.includes(image)
      ? selectedImages.filter((img) => img !== image)
      : [...selectedImages, image];
    dispatch(setSelectedImages(newSelection));
  };

  const openModal = (index) => {
    setModalStateLocal({ isOpen: true, currentImageIndex: index });
  };

  const closeModal = () => {
    setModalStateLocal({ isOpen: false, currentImageIndex: 0 });
  };

  const nextModalImage = () => {
    setModalStateLocal((prevState) => ({
      ...prevState,
      currentImageIndex:
        (prevState.currentImageIndex + 1) % imageList.length,
    }));
  };

  const prevModalImage = () => {
    setModalStateLocal((prevState) => ({
      ...prevState,
      currentImageIndex:
        (prevState.currentImageIndex - 1 + imageList.length) % imageList.length,
    }));
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + imagesPerSlide >= imageList.length ? 0 : prevIndex + imagesPerSlide
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex - imagesPerSlide < 0
        ? Math.max(imageList.length - imagesPerSlide, 0)
        : prevIndex - imagesPerSlide
    );
  };

  const totalSlides = Math.ceil(imageList.length / imagesPerSlide);

  return (
    <div className="relative w-full mx-auto pb-5">
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${(currentIndex / imagesPerSlide) * 100}%)` }}
        >
          {imageList.map((img, index) => (
            <div
              key={index}
              className={`relative flex-shrink-0 p-2 ${
                deleteMode ? "cursor-default" : "cursor-pointer"
              }`}
              style={{ width: `${100 / imagesPerSlide}%` }}
              onClick={() => !deleteMode && openModal(index)}
            >
              {deleteMode && (
                <div className="absolute top-2 left-2 z-10">
                  <input
                    type="checkbox"
                    checked={selectedImages.includes(img)}
                    onChange={() => handleCheckboxChange(index)}
                    className="m-2 rounded-xl w-4 h-4 accent-[#EC221F]"
                  />
                </div>
              )}
              <img
                src={img}
                alt={`Carousel ${index + 1}`}
                className={`w-full h-40 object-cover rounded-lg`}
              />
            </div>
          ))}
        </div>
      </div>

      {imageList.length > imagesPerSlide && (
        <>
          {currentIndex > 0 && (
            <ArrowButton onClick={prevSlide} position="left" />
          )}
          {currentIndex + imagesPerSlide < imageList.length && (
            <ArrowButton onClick={nextSlide} position="right" />
          )}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex space-x-2 p-1 bg-[#bfbfbfb9] rounded-full">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index * imagesPerSlide)}
                className={`w-2 h-2 rounded-full ${
                  Math.floor(currentIndex / imagesPerSlide) === index
                    ? "bg-black"
                    : "bg-gray-400"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      <ImageModal
        isOpen={modalState.isOpen}
        currentImageIndex={modalState.currentImageIndex}
        imageList={imageList}
        onClose={closeModal}
        onNext={nextModalImage}
        onPrev={prevModalImage}
      />
    </div>
  );
}

const ArrowButton = ({ onClick, position }) => (
  <button
    onClick={onClick}
    className={`absolute ${position}-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition-all duration-200`}
    style={{ top: "calc(50% - 10px)" }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={position === "left" ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"}
      />
    </svg>
  </button>
);
